import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ownerApi } from './ownerClient'
import type { MeResponse, Order, Page } from '../types'

// Staff sign-in is the unified OTP flow (LoginPage → /api/otp/verify, D-084); the owner console
// no longer has its own login call — the session is seeded there via setStaffSession.

async function fetchMe(): Promise<MeResponse> {
  const { data } = await ownerApi.get<MeResponse>('/auth/me')
  return data
}

/**
 * The signed-in staff member's own identity (id/tenant/role). Stable for the whole session, so it
 * never refetches (`staleTime: Infinity`). The board uses `userId` to filter to "my deliveries"
 * (`order.assignedStaffId === userId`) — the session store holds only email/role, not the id.
 */
export function useMe(enabled: boolean) {
  return useQuery({ queryKey: ['me'], queryFn: fetchMe, enabled, staleTime: Infinity })
}

async function fetchBoardOrders(): Promise<Order[]> {
  const { data } = await ownerApi.get<Order[]>('/orders')
  return data
}

/**
 * Active orders for the live board (newest first). Real-time updates arrive over SSE
 * (see `useOrderStream`); this slow ~30s poll is only a fallback that reconciles the cache
 * against the server if the stream drops.
 */
export function useBoardOrders(enabled: boolean) {
  return useQuery({
    queryKey: ['board', 'orders'],
    queryFn: fetchBoardOrders,
    enabled,
    refetchInterval: enabled ? 30000 : false,
  })
}

const HISTORY_PAGE_SIZE = 20

async function fetchOrderHistory(page: number): Promise<Page<Order>> {
  const { data } = await ownerApi.get<Page<Order>>('/orders/history', {
    params: { page, size: HISTORY_PAGE_SIZE },
  })
  return data
}

/**
 * Owner order history (§16 Phase 6b): all orders (any status), newest first, paginated.
 * `keepPreviousData` avoids a flash of empty list when paging. 0-based `page`.
 */
export function useOrderHistory(page: number, enabled: boolean) {
  return useQuery({
    queryKey: ['history', 'orders', page],
    queryFn: () => fetchOrderHistory(page),
    enabled,
    placeholderData: keepPreviousData,
  })
}

/**
 * `from` is the status the screen showed when the button was pressed. The server refuses (409) if
 * the order has moved on since, so a stale card can't act on an order nobody is looking at, and
 * treats a repeat (already at `status`) as success (D-111).
 */
export async function updateOrderStatus(id: string, status: string, from?: string): Promise<Order> {
  const { data } = await ownerApi.patch<Order>(`/orders/${id}/status`, { status, from })
  return data
}

/** Assign or clear the physical leg-tag / token number on one order line (D-102, per line); '' clears it. */
export async function updateOrderItemTag(orderId: string, itemId: string, tagNumber: string): Promise<Order> {
  const { data } = await ownerApi.patch<Order>(`/orders/${orderId}/items/${itemId}/tag`, { tagNumber })
  return data
}

/** Name (or clear, with `null`) the staff member who will deliver a delivery order. OWNER-only. */
export async function updateOrderAssignee(id: string, staffId: string | null): Promise<Order> {
  const { data } = await ownerApi.patch<Order>(`/orders/${id}/assignee`, { staffId })
  return data
}

/**
 * Record (or clear, with `null`) the real weighed amount on a catch-weight order line. Re-prices the
 * line for a priced shop; any staff role may weigh. Returns the recomputed order.
 */
export async function updateOrderItemWeight(orderId: string, itemId: string, actualWeight: number | null): Promise<Order> {
  const { data } = await ownerApi.patch<Order>(`/orders/${orderId}/items/${itemId}/weight`, { actualWeight })
  return data
}

/** Mints the httpOnly stream cookie (staff-authed) so the board's EventSource can connect (§7, D-029). */
export async function authorizeStream(): Promise<void> {
  await ownerApi.post('/stream/auth')
}
